import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FiFolder, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useTheme } from '../../../../contexts/ThemeContext';

const DirectoryNode: React.FC<NodeProps> = ({ data, selected }) => {
  const { darkMode } = useTheme();

  const getNodeBgColor = () => {
    if (data.isFocusNode) {
      return darkMode ? 'bg-blue-900' : 'bg-blue-100';
    }
    return darkMode ? 'bg-gray-800' : 'bg-white';
  };

  const getNodeBorderColor = () => {
    if (data.isFocusNode) {
      return 'border-blue-500';
    }
    return darkMode ? 'border-gray-600 border-yellow-500' : 'border-gray-300 border-yellow-500';
  };

  // Determine handle positions based on layout direction
  const sourcePosition = data.layoutDirection === 'BT' ? Position.Top : Position.Bottom;
  const targetPosition = data.layoutDirection === 'BT' ? Position.Bottom : Position.Top;

  return (
    <div className={`px-4 py-2 rounded-md shadow-md border-2 ${getNodeBgColor()} ${getNodeBorderColor()} transition-all duration-200 ${
      selected ? 'transform scale-110 shadow-lg z-10 ring-2 ring-blue-500' : ''
    } ${data.isInHydrationPlan === false ? 'opacity-70' : ''}`}
    style={{
      minWidth: '180px',
      backdropFilter: 'blur(8px)',
      boxShadow: selected ? 
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(59, 130, 246, 0.5)' : 
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }}>
      <Handle 
        type="target" 
        position={targetPosition} 
        className="w-2 h-2 !bg-gray-400"
        id={data.layoutDirection === 'BT' ? 'bottom' : 'top'} 
        style={{
          border: '2px solid',
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          background: '#F59E0B' // Yellow for directories
        }}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FiFolder className="text-yellow-500" />
          <span className="ml-2 font-mono text-sm truncate" title={data.path}>
            {data.label}
          </span>
          <button 
            className="ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              data.onToggleExpand();
            }}
          >
            {data.expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
          </button>
        </div>
        {/* Add directory badge */}
        <span className={`text-xs px-1.5 py-0.5 rounded-full ml-2 ${
          darkMode ? 'bg-yellow-800 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
        }`}>
          dir
        </span>
      </div>
      {data.childCount > 0 && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {data.childCount} {data.childCount === 1 ? 'item' : 'items'}
        </div>
      )}
      <Handle 
        type="source" 
        position={sourcePosition} 
        className="w-2 h-2 !bg-gray-400"
        id={data.layoutDirection === 'BT' ? 'top' : 'bottom'} 
        style={{
          border: '2px solid',
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          background: '#F59E0B' // Yellow for directories
        }}
      />
    </div>
  );
};

export default DirectoryNode;
